import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ToolTip } from '@repo/common';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import { JOB_COLOR, JOB_LABEL, buildDashboard, hms } from '../../components/TwoDays/model/dashboard';
import useMedia, { MOBILE } from '../../components/TwoDays/useMedia';
import { cn } from '../../components/TwoDays/model/derive';
import '../../components/TwoDays/dashboard.css';
import DotMap from '../../components/TwoDays/DotMap';

/**
 * W7 Dashboard (Workboard Dashboard Flows) — 어두운 Command panel(Time left · 바통 Hero · 48h 릴레이 리본)
 * → Need actions(보는 사람만) + Team progress / Who needs help → Team now 레인.
 * Need actions 말고는 팀 모두에게 같은 내용. 시계는 1초마다, 나머지는 화면 시각(1분)이 바뀔 때 다시 계산한다.
 * 계산은 model/dashboard.js.
 */

// 디자인 팔레트 (dashboard-content · --m-*)
const C = {
  canvas: '#F4F5F2',
  surface: '#FFFFFF',
  ink: '#111311',
  sub: '#676B67',
  brand: '#0E6B4A',
  signal: '#71E6B5',
  warn: '#FFCB45',
  crit: '#FF4D5E',
  line: '#E4E7E2',
  dim: 'rgba(255, 255, 255, 0.6)',
  dim2: 'rgba(255, 255, 255, 0.12)',
};
const HI_RING = '0 0 0 2px #FFFFFF';

/** 직무 색 (시안 dashboard.js) — 칸 · 범례 · 모바일 직무 카드 */
const jobC = (jk) => JOB_COLOR[jk] ?? '#b1ada8';
const onInk = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, ${C.ink})`;
const alpha = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;
const HATCH = `repeating-linear-gradient(135deg, ${C.crit} 0 2px, transparent 2px 5px)`;
const AV_BG = '#2B2E2B';
const RHB = 28;
const RGB = 8;
const TOP = 24;

/** 히어로 범례 (Version B) */
const HandoffLegend = ({ d, lateOn, onLate }) => {
  const used = new Set(d.bars.flatMap((b) => [b.jk, ...b.parts.map((p) => p.jk)]));
  const holders = ['planner', 'designer', 'developer', 'marketer']
    .filter((k) => k !== 'marketer' || used.has(k))
    .concat('lead');
  return (
    <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
      <span style={{ fontSize: 13, lineHeight: '140%', fontWeight: 500, color: C.dim }}>Hand-offs · 48 hours</span>
      <div className='flex flex-wrap items-center gap-x-4 gap-y-2' style={{ fontSize: 12, lineHeight: '140%', color: C.dim }}>
        {holders.map((k) => (
          <span key={k} className='flex items-center gap-1.5' style={{ color: '#FFFFFF', fontWeight: 500 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: jobC(k) }} />
            {JOB_LABEL[k]}
          </span>
        ))}
        <span aria-hidden style={{ width: 1, height: 12, background: 'rgba(255, 255, 255, 0.16)' }} />
        <span className='flex items-center gap-1.5'>
          <span style={{ width: 18, height: 10, boxSizing: 'border-box', borderRadius: 100, border: '1px dashed rgba(255, 255, 255, 0.56)' }} />
          Planned · time left to due
        </span>
        <span className='flex items-center gap-1.5'>
          <span style={{ width: 18, height: 10, boxSizing: 'border-box', borderRadius: 100, border: `1px solid ${C.crit}`, background: HATCH }} />
          Late
        </span>
        <span className='flex items-center gap-1.5'>
          <span style={{ width: 2, height: 12, background: '#FFFFFF' }} />
          Now
        </span>
      </div>
      {d.nLate > 0 && (
        <button
          type='button'
          onClick={onLate}
          aria-pressed={lateOn}
          title='Highlight late tasks only'
          className='ml-auto inline-flex items-center gap-1.5'
          style={{
            height: 28,
            padding: '0 10px',
            borderRadius: 8,
            border: `1px solid ${C.crit}`,
            background: lateOn ? C.crit : 'transparent',
            color: lateOn ? C.ink : C.crit,
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
          {d.nLate} late
        </button>
      )}
    </div>
  );
};

/**
 * 히어로 그래프 (W7 Version B · hand-off bars) — 태스크 1개 = 막대 1줄 (만든 순서), 고정 48h 축.
 * 막대는 넘길 때마다 나뉜다(담당자 working → 받는 사람 reviewing → 담당자 reworking …). 칸마다 그 사람의 직무 색 + 아바타.
 * 담당자 칸마다 따로 호버 → 짧은 카드(태스크 · 사람 · 하던 일 · 시간). 계획 막대 · 남은 시간 꼬리는 막대 카드(담당자 · 상태 · 마감).
 * 호버하면 그 태스크와 앞 · 뒤 태스크만 진하게, 나머지는 흐리게. 누르면 태스크로.
 * 호버 상태는 이 컴포넌트 안에만 둔다 — 대시보드 전체를 다시 그리지 않게.
 */
const HandoffBars = React.memo(({ d, cross, tab, onTask }) => {
  const trackRef = useRef(null);
  const [bw, setBw] = useState(1000);
  const [sel, setSel] = useState(null); // { id, part } — part 가 null 이면 막대 전체
  const [lateOn, setLateOn] = useState(false);
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    setBw(el.offsetWidth || 1000);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setBw(e.contentRect.width || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const ticks = tab ? d.ticksTab : d.ticks;
  const n = d.bars.length;
  const tracksH = n * RHB + Math.max(0, n - 1) * RGB;
  const selB = sel ? d.bars.find((b) => b.id === sel.id) : null;
  const selIdx = selB ? d.bars.indexOf(selB) : -1;
  const rel = selB ? new Set([selB.id, ...selB.rel]) : null;
  const selPart = selB && sel.part != null ? selB.parts[sel.part] : null;
  const card = selPart ? selPart.card : selB?.card;
  const pct = (v) => `${v.toFixed(2)}%`;
  const anchorX = selPart ? selPart.L + selPart.W : selB ? selB.endX : 0;
  const anchorS = selPart ? selPart.L : selB ? selB.L : 0;
  const keyOpen = (id) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTask(id);
    }
  };

  if (!n) {
    return <span style={{ fontSize: 13, color: C.dim }}>Add tasks to see who holds each one.</span>;
  }
  return (
    <div className='relative flex flex-col gap-3'>
      <HandoffLegend d={d} lateOn={lateOn} onLate={() => setLateOn((v) => !v)} />
      <div ref={trackRef} style={{ position: 'relative', minWidth: 0 }} onMouseLeave={() => setSel(null)}>
        <div style={{ height: TOP }} />
        {ticks
          .filter((k) => k.major)
          .map((k) => (
            <span
              key={`g${k.x}`}
              aria-hidden
              style={{ position: 'absolute', left: pct(k.x), top: TOP, height: tracksH, borderLeft: '1px dashed rgba(255, 255, 255, 0.1)', pointerEvents: 'none' }}
            />
          ))}
        <div className='flex flex-col' style={{ gap: RGB }}>
          {d.bars.map((b, i) => {
            const op = lateOn && !b.late ? 0.2 : rel && !rel.has(b.id) ? 0.35 : 1;
            const strong = (selB && selB.id === b.id && sel.part == null) || cross === b.id;
            const ring = strong ? '2px solid rgba(255, 255, 255, 0.6)' : b.urgent ? '1px solid rgba(255, 255, 255, 0.4)' : 'none';
            const fade = { opacity: op, transition: 'opacity 0.15s ease' };
            const R = Math.max(b.endX, b.L + (b.plan ? b.planW : 0));
            // 이름표 — 막대(점선 꼬리 포함) 끝 오른쪽에 들어가면 오른쪽, 아니면 시작 왼쪽. 둘 다 모자라면 넓은 쪽에서 말줄임
            const roomR = bw - (R / 100) * bw - 8;
            const roomL = (b.L / 100) * bw - 8;
            const wOf = (len) => len * 6.3;
            const full = b.title.length + b.lateTxt.length;
            const right = wOf(full) <= roomR || (wOf(full) > roomL && roomR >= roomL);
            const maxC = Math.floor((right ? roomR : roomL) / 6.3) - b.lateTxt.length;
            const title = b.title.length > maxC ? `${b.title.slice(0, Math.max(4, maxC - 1)).trimEnd()}…` : b.title;
            let prev = -1e9;
            return (
              <div key={b.id} className='db-rise' style={{ '--d': `${200 + Math.min(i, 12) * 40}ms`, position: 'relative', height: RHB }}>
                {b.plan && (
                  <>
                    <span
                      style={{ position: 'absolute', left: pct(b.L), width: pct(b.planW), top: 0, bottom: 0, boxSizing: 'border-box', borderRadius: 100, border: `1px dashed ${alpha(jobC(b.jk), 56)}`, ...fade }}
                    />
                    <span
                      style={{ position: 'absolute', left: `calc(${pct(b.L)} + 3px)`, top: 3, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: AV_BG, color: 'rgba(255, 255, 255, 0.72)', fontSize: 9, fontWeight: 500, zIndex: 2, ...fade }}
                    >
                      {b.ini}
                    </span>
                  </>
                )}
                {b.ghost && b.ghost.W > 0.3 && (
                  <span
                    style={{ position: 'absolute', left: pct(b.ghost.L), width: pct(b.ghost.W), top: 3, bottom: 3, boxSizing: 'border-box', border: `1px dashed ${alpha(jobC(b.ghost.jk), 40)}`, borderLeft: 'none', borderRadius: '0 100px 100px 0', ...fade }}
                  />
                )}
                {b.parts.map((g, k) => {
                  const c = jobC(g.jk);
                  const firstP = k === 0;
                  const lastP = k === b.parts.length - 1 && !b.late;
                  const on = !!selB && selB.id === b.id && sel.part === k;
                  return (
                    <span
                      key={g.k}
                      role='button'
                      tabIndex={0}
                      aria-label={`${g.card.title} · ${g.card.who} · ${g.card.when}`}
                      onMouseEnter={() => setSel({ id: b.id, part: k })}
                      onFocus={() => setSel({ id: b.id, part: k })}
                      onBlur={() => setSel(null)}
                      onClick={() => onTask(b.id)}
                      onKeyDown={keyOpen(b.id)}
                      style={{
                        position: 'absolute',
                        left: pct(g.L),
                        width: pct(g.W),
                        top: 0,
                        bottom: 0,
                        boxSizing: 'border-box',
                        background: b.done ? onInk(c, 50) : c,
                        borderRadius: `${firstP ? 14 : 0}px ${lastP ? 14 : 0}px ${lastP ? 14 : 0}px ${firstP ? 14 : 0}px`,
                        borderLeft: firstP ? 'none' : `2px solid ${C.ink}`,
                        outline: on ? '2px solid rgba(255, 255, 255, 0.6)' : 'none',
                        outlineOffset: 2,
                        zIndex: on ? 4 : 3,
                        cursor: 'pointer',
                        ...fade,
                      }}
                    />
                  );
                })}
                {b.tail && (
                  <span
                    aria-hidden
                    style={{ position: 'absolute', left: pct(b.tail.L), width: pct(b.tail.W), top: 0, bottom: 0, boxSizing: 'border-box', border: `1px solid ${C.crit}`, borderRadius: '0 100px 100px 0', background: HATCH, zIndex: 1, ...fade }}
                  />
                )}
                {/* 칸마다 아바타 1개 — 칸이 넓으면 왼쪽 안, 좁으면 가운데. 앞 아바타와 6px 넘게 겹치지 않게 */}
                {b.parts.map((g) => {
                  const a = (g.L / 100) * bw;
                  const w = (g.W / 100) * bw;
                  let x = w >= 28 ? a + 3 : a + w / 2 - 11;
                  x = Math.max(x, prev + 16);
                  prev = x;
                  const c = jobC(g.jk);
                  return (
                    <span
                      key={`${g.k}-av`}
                      aria-hidden
                      style={{ position: 'absolute', left: `calc(${pct(g.L)} + ${(x - a).toFixed(1)}px)`, top: 3, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: AV_BG, boxShadow: `0 0 0 2px ${b.done ? onInk(c, 50) : c}`, color: '#FFFFFF', fontSize: 9, fontWeight: 500, zIndex: 5, pointerEvents: 'none', ...fade }}
                    >
                      {g.ini}
                    </span>
                  );
                })}
                {/* 막대 전체 — 계획 막대 · 남은 시간 꼬리 · 늦음에 호버 */}
                <div
                  role='button'
                  tabIndex={0}
                  aria-label={`${b.title} · ${b.st}${b.lateTxt ? ` ·${b.lateTxt} late` : ''}`}
                  onMouseEnter={() => setSel({ id: b.id, part: null })}
                  onFocus={() => setSel({ id: b.id, part: null })}
                  onBlur={() => setSel(null)}
                  onClick={() => onTask(b.id)}
                  onKeyDown={keyOpen(b.id)}
                  style={{ position: 'absolute', left: pct(b.L), width: pct(Math.max(0.6, R - b.L)), top: 0, bottom: 0, borderRadius: 100, outline: ring, outlineOffset: 2, zIndex: 2, cursor: 'pointer' }}
                />
                <span
                  style={{
                    position: 'absolute',
                    left: right ? `calc(${pct(R)} + 8px)` : `calc(${pct(b.L)} - 8px)`,
                    top: '50%',
                    transform: right ? 'translateY(-50%)' : 'translate(-100%, -50%)',
                    fontSize: 12,
                    lineHeight: '140%',
                    fontWeight: 500,
                    color: b.done ? 'rgba(255, 255, 255, 0.5)' : '#FFFFFF',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    ...fade,
                  }}
                >
                  {title}
                  <span style={{ color: C.crit, fontVariantNumeric: 'tabular-nums' }}>{b.lateTxt}</span>
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ position: 'relative', height: 28, marginTop: 8 }}>
          {ticks.map((k) => (
            <React.Fragment key={`t${k.x}`}>
              <span style={{ position: 'absolute', left: pct(k.x), top: 0, height: k.major ? 8 : 4, borderLeft: '1px solid rgba(255, 255, 255, 0.24)' }} />
              <span
                style={{
                  position: 'absolute',
                  left: pct(k.x),
                  top: 10,
                  transform: k.edge === 'start' ? 'none' : k.edge === 'end' ? 'translateX(-100%)' : 'translateX(-50%)',
                  fontSize: 12,
                  lineHeight: '140%',
                  color: 'rgba(255, 255, 255, 0.4)',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {k.label}
              </span>
            </React.Fragment>
          ))}
        </div>
        {d.showNow && (
          <>
            {/* 지난 시간은 어둡게 */}
            <span aria-hidden style={{ position: 'absolute', left: 0, top: TOP, height: tracksH, width: pct(d.nowX), background: 'rgba(17, 19, 17, 0.4)', zIndex: 4, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(d.nowX), zIndex: 5, pointerEvents: 'none' }}>
              <span style={{ position: 'absolute', top: 20, bottom: 0, left: -1, width: 2, background: '#FFFFFF' }} />
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: d.nowX > 92 ? 'translateX(-100%)' : d.nowX < 8 ? 'none' : 'translateX(-50%)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 20,
                  padding: '0 8px',
                  borderRadius: 8,
                  background: '#FFFFFF',
                  color: C.ink,
                  fontSize: 12,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {d.nowLabel}
              </span>
            </div>
          </>
        )}
        {card && (
          <div
            role='tooltip'
            className='db-pop shadow-popup-01'
            style={{
              position: 'absolute',
              left: anchorX < 72 ? `calc(${pct(anchorX)} + 12px)` : `max(0px, calc(${pct(anchorS)} - 232px))`,
              top: Math.max(0, Math.min(TOP + tracksH - 96, TOP + selIdx * (RHB + RGB) - 30)),
              zIndex: 7,
              width: 220,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '10px 12px',
              background: C.surface,
              color: C.ink,
              border: '1px solid color-mix(in srgb, #009e4f 8%, transparent)',
              borderRadius: 12,
              pointerEvents: 'none',
            }}
          >
            <span className='truncate' style={{ fontSize: 13, lineHeight: '140%', fontWeight: 500 }}>
              {card.title}
            </span>
            <span style={{ fontSize: 12, lineHeight: '140%', color: C.sub }}>{card.who}</span>
            <span className='flex flex-wrap gap-1.5' style={{ fontSize: 12, lineHeight: '140%', fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: C.sub }}>{card.when}</span>
              {card.late && <span className='font-medium text-text-failed'>{card.late}</span>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * 모바일 히어로 — 직무 카드 (요약 + 48h 미니 막대와 Now). 누르면 그 직무 태스크 바텀시트, 행을 누르면 태스크로.
 */
const JobCards = ({ d, onTask }) => {
  const [open, setOpen] = useState(null); // 직무 key
  const sheet = open ? d.jobCards.find((c) => c.jk === open) : null;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const first = d.ticks[0];
  const last = d.ticks[d.ticks.length - 1];
  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between gap-2' style={{ fontSize: 12, lineHeight: '140%', color: C.dim }}>
        <span style={{ fontWeight: 500 }}>Tasks by job · 48 hours</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {first?.label.replace(' · ', ' ')} → {last?.label.replace(' · ', ' ')}
        </span>
      </div>
      {d.jobCards.map((r) => (
        <button
          key={r.jk}
          type='button'
          onClick={() => setOpen(r.jk)}
          className='flex w-full flex-col text-left'
          style={{ gap: 10, minHeight: 56, padding: '14px 16px', borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)', color: '#FFFFFF' }}
        >
          <div className='flex w-full items-center gap-2'>
            <span style={{ width: 10, height: 10, flexShrink: 0, borderRadius: 3, background: jobC(r.jk) }} />
            <span style={{ fontSize: 15, lineHeight: '130%', fontWeight: 500 }}>{r.job}</span>
            <span className='min-w-0 flex-1 truncate' style={{ fontSize: 12, lineHeight: '135%', color: C.dim }}>
              {r.sum}
              {r.lateTxt && <span style={{ color: C.crit }}> · {r.lateTxt}</span>}
            </span>
            <ChevronIcon aria-hidden className='h-4 w-4 shrink-0' style={{ color: C.dim }} />
          </div>
          <div style={{ position: 'relative', width: '100%', height: 10, borderRadius: 3, background: 'rgba(255, 255, 255, 0.06)' }}>
            {r.mini.map((m) => (
              <span
                key={m.k}
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${m.L.toFixed(2)}%`,
                  width: `${m.W.toFixed(2)}%`,
                  boxSizing: 'border-box',
                  borderRadius: 2,
                  background: m.kind === 'late' ? C.crit : m.kind === 'plan' ? 'transparent' : m.kind === 'done' ? onInk(jobC(r.jk), 40) : jobC(r.jk),
                  border: m.kind === 'plan' ? `1px dashed ${jobC(r.jk)}` : 'none',
                }}
              />
            ))}
            {d.showNow && (
              <span style={{ position: 'absolute', top: -4, bottom: -4, left: `${d.nowX.toFixed(2)}%`, width: 2, marginLeft: -1, background: '#FFFFFF' }} />
            )}
          </div>
        </button>
      ))}
      {sheet &&
        ReactDOM.createPortal(
          <div
            role='presentation'
            onClick={() => setOpen(null)}
            className='db-scrim fixed inset-0 z-[960] flex items-end bg-[rgba(0,0,0,0.4)]'
          >
            <div
              role='dialog'
              aria-modal='true'
              aria-label={`${sheet.job} tasks`}
              onClick={(e) => e.stopPropagation()}
              className='db-sheet-up flex max-h-[80%] w-full flex-col gap-3 overflow-auto rounded-t-[24px] bg-grayscale-gray00 px-5 pb-7 pt-3 shadow-popup-01'
            >
              <span className='h-1 w-9 shrink-0 self-center rounded-full bg-grayscale-gray03' />
              <span className='text-[16px] font-medium leading-[140%] text-text-primary'>{sheet.job} tasks</span>
              <div className='flex flex-col overflow-hidden rounded-xl border border-green-deep03/[0.12]'>
                {sheet.rows.map((t) => (
                  <button
                    key={t.id}
                    type='button'
                    onClick={() => {
                      setOpen(null);
                      onTask(t.id);
                    }}
                    className='-mt-px flex min-h-11 flex-col gap-0.5 border-t border-green-deep03/[0.12] px-3.5 py-3 text-left hover:bg-green-deep03/[0.03]'
                  >
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{t.title}</span>
                    <span className='text-[13px] leading-[140%] text-text-secondary'>{t.who}</span>
                    <span className='flex flex-wrap gap-2 text-[13px] leading-[140%]'>
                      <span
                        className={cn(
                          'font-medium',
                          t.tone === 'late' ? 'text-text-failed' : t.tone === 'done' ? 'text-text-secondary' : 'text-text-primary',
                        )}
                      >
                        {t.st}
                      </span>
                      <span className='text-text-secondary' style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {t.due}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <button
                type='button'
                onClick={() => setOpen(null)}
                className='h-11 shrink-0 rounded-full bg-green-deep03/[0.08] text-[14px] font-medium text-text-primary'
              >
                Close
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

/**
 * Team now 마감 라벨 — 마감 시각마다 점선 1개. 라벨은 막대 안에 들어가고 앞 라벨과 8px 이상 떨어질 때만 보인다.
 * 그 사이 점선은 앞 라벨에 +n (첫 라벨 앞의 것은 첫 라벨에). 긴 태스크 이름은 말줄임. (시안 dashboard.js · 글자 폭 ≈ 6.3px)
 */
const dueLabels = (dues, bw, maxC) => {
  const cut = (t) => (t.length > maxC ? `${t.slice(0, maxC - 1).trimEnd()}…` : t);
  const groups = [];
  const pend = [];
  let prevR = -1e9;
  dues.forEach((u) => {
    const px = (u.x / 100) * bw;
    const w = `“${cut(u.titles[0])}” due 00:00 +9`.length * 6.3;
    const L = px - w / 2;
    const R = px + w / 2;
    if (L >= 0 && R <= bw && L >= prevR + 8) {
      groups.push({ u, n: u.titles.length });
      prevR = R;
    } else if (groups.length) groups[groups.length - 1].n += u.titles.length;
    else pend.push(u);
  });
  if (groups.length) groups[0].n += pend.reduce((a, u) => a + u.titles.length, 0);
  return groups.map((g) => ({
    k: g.u.k,
    x: g.u.x,
    label: `“${cut(g.u.titles[0])}” due ${g.u.at}${g.n > 1 ? ` +${g.n - 1}` : ''}`,
  }));
};

/** 남은 시간 — 1초마다 줄어든다 (목 보드는 고정 시각에서 흐른 만큼) */
const LiveClock = ({ target, now }) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    setT(0);
    if (!target) return undefined;
    const start = Date.now();
    const id = window.setInterval(() => setT(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [target, now]);
  return <>{target ? hms((target - now) / 1000 - t) : '00:00:00'}</>;
};

const Label = ({ children, style }) => (
  <span style={{ fontSize: 13, lineHeight: '140%', fontWeight: 500, color: C.dim, ...style }}>
    {children}
  </span>
);

/** Need actions 행의 버튼 이름 — Inbox 항목 종류별 */
const CTA = {
  checkpoint: 'Check in',
  missed: 'Check in late',
  assigned: 'Accept',
  question: 'Reply',
  returned: 'Reply',
  review: 'Review',
  changes: 'Open',
  due: 'Open',
};

const Section = ({ children, style, className, d = 0 }) => (
  <section className={cn('db-rise', className)} style={{ '--d': `${d}ms`, ...style }}>
    {children}
  </section>
);

const MAP_W = 1600;
const MAP_H = 1100;
/** 시안 지도(1600 × 1100) 위 점선 삼각형 — 한국 · 인도 · 베트남 */
const CITY = { korea: [1339.6, 248.6], india: [567.2, 548.6], vietnam: [934, 783.6] };

/**
 * 배경 점 지도 자리 — 지도(1600 × 1100) 전체가 패널(w × h) 안에 다 보이게 줄여 가운데에.
 * 돌려주는 값은 DotMap 의 view — 지도 좌표 → 패널 좌표 (x * s + ox, y * s + oy)
 */
const mapView = (panel) => {
  if (!panel?.w || !panel?.h) return null;
  const s = Math.min(panel.w / MAP_W, panel.h / MAP_H);
  return {
    w: panel.w,
    h: panel.h,
    s,
    ox: (panel.w - MAP_W * s) / 2,
    // 시안보다 조금 아래로 (패널 높이의 8%)
    oy: (panel.h - MAP_H * s) / 2 + panel.h * 0.08,
  };
};

/**
 * Team now 레인 하나 — 호버 카드는 이 레인의 상태로만 켜져 대시보드 전체를 다시 그리지 않는다 (렉 방지).
 * 카드는 body 에 고정 위치로 띄워 레인 아래 여백·페이지 스크롤 높이를 늘리지 않는다 (아래가 모자라면 위로).
 */
const TeamNowLane = React.memo(({ l, i, hi, mobile, v, mobileOpen, onMobileToggle, viewTasks }) => {
  const laneRef = useRef(null);
  const [hov, setHov] = useState(false);
  const [pos, setPos] = useState(null);
  const toggle = () => (mobile ? onMobileToggle() : setHov((o) => !o));
  useLayoutEffect(() => {
    if (!hov || mobile) {
      setPos(null);
      return undefined;
    }
    const place = () => {
      const r = laneRef.current?.getBoundingClientRect();
      if (!r) return;
      const h = 236; // 카드 높이 어림 — 아래 공간이 모자라면 레인 위로
      const below = r.bottom - 4;
      const top = below + h > window.innerHeight - 16 && r.top - h - 4 >= 16 ? r.top - h + 4 : below;
      setPos({ left: Math.min(r.left + 8, window.innerWidth - 264 - 16), top });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [hov, mobile]);
        const open = mobile ? mobileOpen : hov;
        return (
          <div
            role={l.left ? undefined : 'button'}
            tabIndex={l.left ? undefined : 0}
            ref={laneRef}
            onClick={() => !l.left && toggle()}
            onKeyDown={(e) => e.key === 'Enter' && !l.left && toggle()}
            // 데스크톱 · 태블릿은 레인에 마우스를 올리면 프로필 카드 (카드로 옮겨도 그대로) — 이 레인만 다시 그린다
            onMouseEnter={() => !mobile && !l.left && setHov(true)}
            onMouseLeave={() => !mobile && setHov(false)}
            className='db-hi hover:bg-[color-mix(in_srgb,#0E6B4A_4%,transparent)]'
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: v.laneCols,
              gap: v.laneGap,
              alignItems: 'center',
              minHeight: 56,
              padding: 8,
              margin: '0 -8px',
              borderTop: `1px solid ${C.line}`,
              borderRadius: 8,
              background: hi ? 'color-mix(in srgb, #0E6B4A 8%, transparent)' : 'transparent',
              opacity: l.left ? 0.5 : 1,
              cursor: l.left ? 'default' : 'pointer',
              zIndex: open ? 5 : 'auto',
            }}
          >
            <div title={l.left ? `${l.name} left the project` : undefined} className='flex min-w-0 items-center gap-3'>
              <span
                style={{
                  position: 'relative',
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  background: C.canvas,
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                {l.ini}
                {!l.left && (
                  <span
                    style={{
                      position: 'absolute',
                      right: -1,
                      bottom: -1,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: l.online ? C.signal : C.surface,
                      boxShadow: l.online
                        ? `0 0 0 2px ${C.surface}`
                        : `inset 0 0 0 1.5px ${C.sub}, 0 0 0 2px ${C.surface}`,
                    }}
                  />
                )}
              </span>
              <div className='flex min-w-0 flex-col'>
                <span className='flex items-center gap-2 whitespace-nowrap text-[14px] font-medium leading-[135%]'>
                  {l.name}
                  {l.isYou && (
                    <span className='text-[12px] font-normal' style={{ color: C.sub }}>
                      You
                    </span>
                  )}
                </span>
                <span
                  className='truncate text-[12px] leading-[135%]'
                  style={{ color: C.sub, fontVariantNumeric: 'tabular-nums' }}
                >
                  {l.time}
                </span>
              </div>
            </div>
            <div
              style={{
                order: mobile ? 3 : 0,
                gridColumn: mobile ? '1 / -1' : 'auto',
                position: 'relative',
                height: 16,
                borderRadius: 4,
                background: C.canvas,
                overflow: 'hidden',
              }}
            >
              {(l.wins ?? []).map((w) => (
                <span
                  key={`${w.L}-${w.W}`}
                  className='db-wipe'
                  style={{
                    '--d': `${360 + i * 60}ms`,
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${w.L}%`,
                    width: `${w.W}%`,
                    background: `color-mix(in srgb, ${C.brand} 24%, ${C.surface})`,
                  }}
                />
              ))}
            </div>
            <div
              className='flex min-w-0 flex-col items-end gap-1 text-right'
              style={{ order: mobile ? 2 : 0 }}
            >
              <span
                className='flex items-center gap-2 text-[13px] leading-[135%]'
                style={{
                  fontWeight: l.tone === 'quiet' ? 400 : 500,
                  color: l.tone === 'quiet' ? C.sub : C.ink,
                }}
              >
                {l.tone === 'crit' && (
                  <span className='db-blink h-2 w-2 shrink-0 rounded-full' style={{ background: C.crit }} />
                )}
                {l.tone === 'warn' && (
                  <span className='h-2 w-2 shrink-0 rounded-full' style={{ background: C.warn }} />
                )}
                {l.statusT}
              </span>
              {l.cap && (
                <span
                  className='text-[12px] leading-[135%]'
                  style={{ color: C.sub, fontVariantNumeric: 'tabular-nums' }}
                >
                  {l.cap}
                </span>
              )}
            </div>
            {open && !mobile && l.card && pos && ReactDOM.createPortal(
              <div
                role='dialog'
                aria-label={l.name}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => setHov(true)}
                onMouseLeave={() => setHov(false)}
                className='db-pop shadow-popup-01'
                style={{
                  position: 'fixed',
                  left: pos.left,
                  top: pos.top,
                  zIndex: 1000,
                  width: 264,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  padding: 16,
                  background: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: 16,
                  cursor: 'default',
                }}
              >
                <div className='flex flex-col'>
                  <span className='text-[14px] font-medium leading-[140%]'>{l.name}</span>
                  <span className='text-[12px] leading-[140%]' style={{ color: C.sub }}>
                    {l.job} · {l.card.presence}
                  </span>
                </div>
                <div className='flex flex-col gap-1 rounded-lg px-3 py-2' style={{ background: C.canvas }}>
                  <span className='text-[13px] font-medium leading-[140%]' style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {l.card.timeLine}
                  </span>
                  <span className='text-[12px] leading-[140%]' style={{ color: C.sub }}>
                    {l.card.kstLine}
                  </span>
                </div>
                <div className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-[12px] leading-[140%]'>
                  <span style={{ color: C.sub }}>Now</span>
                  <span style={{ textWrap: 'pretty' }}>{l.card.task}</span>
                  <span style={{ color: C.sub }}>Hours</span>
                  <span>{l.card.hours}</span>
                </div>
                <button
                  type='button'
                  onClick={viewTasks}
                  className='group inline-flex items-center gap-1 self-start text-[13px] font-medium'
                >
                  View tasks
                  <ChevronIcon aria-hidden className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
                </button>
              </div>,
              document.body,
            )}
          </div>
        );
});

const TwoDaysDashboard = () => {
  const ctx = useWorkboardCtx();
  const { data, now, memberById, base } = ctx;
  const navigate = useNavigate();
  const mobile = useMedia(MOBILE);
  // 데스크탑 배치는 화면이 아니라 실제 폭으로 — Inbox 를 옆에 열면 좁아진다
  const rootRef = useRef(null);
  const [rootW, setRootW] = useState(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setRootW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Command 패널 높이 — 배경 지도 크기를 시안 비율로 맞춘다
  // 지도 div 가 그려질 때 부모(패널)를 지켜본다 — 첫 화면에 패널이 늦게 나와도 붙는다
  const [panelEl, setPanelEl] = useState(null);
  const mapRef = useCallback((el) => setPanelEl(el?.parentElement ?? null), []);
  const [panel, setPanel] = useState(null); // { w, h }
  useEffect(() => {
    if (!panelEl || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setPanel({ w: panelEl.offsetWidth, h: panelEl.offsetHeight });
    measure(); // 첫 화면에서 바로 — 관찰 콜백은 다음 프레임에 온다
    const ro = new ResizeObserver(measure);
    ro.observe(panelEl);
    return () => ro.disconnect();
  }, [panelEl]);
  const wideScreen = useMedia('(min-width: 1180px)');
  const wide = rootW == null ? wideScreen : rootW >= 1000;
  const lay = mobile ? 'mob' : wide ? 'desk' : 'tab';
  const desk = lay === 'desk';
  const tab = lay === 'tab';
  const [hover, setHover] = useState(null); // Team now 호버 카드 · 모바일 시트
  const [cross, setCross] = useState(null); // Who needs help ↔ 리본 · 레인 · Blocked 기둥

  const needActions = useMemo(
    () => (ctx.inbox?.actions ?? []).filter((i) => !i.done),
    [ctx.inbox],
  );
  const d = useMemo(
    () => buildDashboard({ data, now, memberById, needActions }),
    [data, now, memberById, needActions],
  );
  const crossRow = d.blk.find((r) => r.taskId === cross);

  useEffect(() => {
    if (!hover || !mobile) return undefined;
    const onKey = (e) => e.key === 'Escape' && setHover(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hover, mobile]);

  const goTask = (taskId, commentId) =>
    navigate(`${base}/tasks/${taskId}`, {
      state: commentId ? { inboxFocus: { focus: 'comment', commentId, key: Date.now() } } : undefined,
    });
  const viewTasks = () => navigate(`${base}/tasks?view=assignee`);

  const lvl = d.clock.lvl;
  const chip =
    lvl === 'warn'
      ? [C.warn, C.ink]
      : lvl === 'danger'
        ? [C.crit, '#FFFFFF']
        : [C.dim2, '#FFFFFF'];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const v = useMemo(() => ({
    gap: desk ? 32 : tab ? 24 : 16,
    radius: mobile ? 20 : 24,
    cpPad: desk ? 32 : tab ? 28 : 20,
    cpGap: desk ? 48 : tab ? 32 : 24,
    heroCols: desk ? '320px minmax(0,1fr) 248px' : tab ? 'minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr)',
    clockFs: mobile ? 44 : 56,
    bigFs: desk ? 44 : tab ? 40 : 32,
    titleFs: desk ? 32 : tab ? 28 : 26,
    secPad: desk ? 32 : tab ? 28 : 20,
    btnH: mobile ? 44 : 32,
    laneCols: desk ? '176px minmax(0,1fr) 224px' : tab ? '152px minmax(0,1fr) 172px' : 'minmax(0,1fr) auto',
    laneGap: desk ? 24 : tab ? 16 : '8px 12px',
  }), [desk, tab, mobile]);
  // 배경 점 지도 — 시안과 같은 배치
  const dotView = mapView(panel);
  const divider = `1px solid ${C.dim2}`;
  const sheetLane = mobile && hover ? d.lanes.find((l) => l.id === hover) : null;
  // Team now 막대 폭(px) 어림 — 라벨이 막대 안에 들어가는지 볼 때 쓴다
  const laneW = Math.max(
    200,
    (rootW ?? 1000) - v.secPad * 2 - (desk ? 176 + 224 + 48 : tab ? 152 + 172 + 32 : 0),
  );
  const dues = dueLabels(d.lDues, laneW, desk ? 26 : tab ? 18 : 14);

  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: v.gap,
        // 넓은 화면에서도 디자인 폭을 넘지 않고, 남는 공간은 오른쪽에 둔다
        width: '100%',
        maxWidth: 1152,
        color: C.ink,
      }}
    >
      {/* 머리 */}
      <div className='db-rise flex flex-col gap-1.5'>
        <span className='flex flex-wrap items-center gap-2 text-[13px] leading-[140%] text-text-secondary'>
          <span
            className={cn('h-2 w-2 rounded-full', d.statusLive && 'db-pulse')}
            style={{ background: d.statusLive ? '#1DC37B' : '#B1ADA8' }}
          />
          <span className='font-medium text-text-primary'>{d.status}</span>
          {data.project.briefTitle ? `· ${data.project.briefTitle}` : ''}
        </span>
        <h1
          className='m-0 font-medium tracking-[-0.01em]'
          style={{ fontSize: desk ? 30 : tab ? 26 : 24, lineHeight: '120%' }}
        >
          Dashboard
        </h1>
      </div>

      {/* Command panel */}
      <Section
        d={60}
        style={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: v.cpGap,
          padding: v.cpPad,
          background: C.ink,
          borderRadius: v.radius,
          color: '#FFFFFF',
        }}
      >
        {/* 배경 — 움직이는 점 지도 위로 팀원들이 있는 곳(한국 · 인도 · 베트남)을 잇는 점선 삼각형 (디자인 장식) */}
        <div
          ref={mapRef}
          aria-hidden
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        >
          {dotView && (
            <>
              <DotMap view={dotView} />
              <svg
                width={dotView.w}
                height={dotView.h}
                style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible' }}
              >
                <polyline
                  className='db-dash'
                  points={[CITY.korea, CITY.vietnam, CITY.india, CITY.korea]
                    .map(([x, y]) => `${x * dotView.s + dotView.ox},${y * dotView.s + dotView.oy}`)
                    .join(' ')}
                  fill='none'
                  stroke={C.signal}
                  strokeOpacity='0.16'
                  strokeWidth='1'
                  strokeDasharray='2 5'
                />
                {[CITY.korea, CITY.india].map(([x, y]) => (
                  <circle
                    key={`${x},${y}`}
                    cx={x * dotView.s + dotView.ox}
                    cy={y * dotView.s + dotView.oy}
                    r='3'
                    fill='#FFFFFF'
                    fillOpacity='0.24'
                  />
                ))}
                <circle
                  className='db-glow'
                  cx={CITY.vietnam[0] * dotView.s + dotView.ox}
                  cy={CITY.vietnam[1] * dotView.s + dotView.oy}
                  r='4'
                  fill={C.signal}
                  fillOpacity='0.5'
                />
              </svg>
            </>
          )}
        </div>
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: v.heroCols,
            gap: desk ? 32 : tab ? '24px 32px' : 20,
            alignItems: 'end',
          }}
        >
          <div style={{ gridColumn: tab ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className='flex items-center gap-2' style={{ minHeight: 24 }}>
              <Label>Time left</Label>
              {d.clock.chip && (
                <span
                  className={cn(lvl === 'danger' && 'db-blink')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 24,
                    padding: '0 8px',
                    borderRadius: 8,
                    background: chip[0],
                    color: chip[1],
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {d.clock.chip}
                </span>
              )}
            </div>
            <span
              role='timer'
              aria-live='off'
              title={d.clock.sub}
              style={{
                fontSize: v.clockFs,
                lineHeight: '100%',
                letterSpacing: '-0.03em',
                fontVariantNumeric: 'tabular-nums',
                color: lvl === 'danger' ? C.crit : lvl === 'warn' ? C.warn : '#FFFFFF',
              }}
            >
              <LiveClock target={d.clock.target} now={now.getTime()} />
            </span>
            <span style={{ fontSize: 13, lineHeight: '140%', color: C.dim }}>{d.clock.cap}</span>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              minWidth: 0,
              paddingLeft: desk ? 32 : 0,
              borderLeft: desk ? divider : 'none',
              paddingTop: desk ? 0 : 20,
              borderTop: desk ? 'none' : divider,
            }}
          >
            {/* 바통 자리 — 가장 급한 태스크 (2줄까지) */}
            <Label>{d.hero.label}</Label>
            <span
              key={d.hero.title}
              className='db-tick'
              title={d.hero.title}
              style={{
                fontSize: v.titleFs,
                lineHeight: '115%',
                letterSpacing: '-0.02em',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textWrap: 'balance',
              }}
            >
              {d.hero.title}
            </span>
            <div title={d.sentence} className='mt-1 flex items-center gap-2'>
              {d.hero.ini && (
                <span
                  style={{
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: C.signal,
                    color: C.ink,
                    fontSize: 10,
                    fontWeight: 500,
                  }}
                >
                  {d.hero.ini}
                </span>
              )}
              <span style={{ fontSize: 15, lineHeight: '130%' }}>{d.hero.ownerLine}</span>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              paddingTop: desk ? 0 : 20,
              borderTop: desk ? 'none' : divider,
            }}
          >
            <Label>{d.hero.handTitle}</Label>
            <div className='flex items-baseline gap-2'>
              <span
                style={{
                  fontSize: v.bigFs,
                  lineHeight: '100%',
                  letterSpacing: '-0.025em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {d.hero.handAt}
              </span>
              <span style={{ fontSize: 13, color: C.dim }}>{d.hero.inT}</span>
            </div>
            <span
              title={d.hero.dev}
              className='mt-1 flex items-center gap-2'
              style={{ fontSize: 13, lineHeight: '140%', color: 'rgba(255, 255, 255, 0.86)' }}
            >
              <span
                className={cn(d.hero.tone === 'crit' && !d.clock.lvl.startsWith('end') && 'db-blink')}
                style={{
                  width: 8,
                  height: 8,
                  flexShrink: 0,
                  borderRadius: '50%',
                  background: d.hero.tone === 'crit' ? C.crit : d.hero.tone === 'ok' ? C.signal : C.sub,
                }}
              />
              {d.hero.trackLine}
              {d.hero.devLine ? ` · ${d.hero.devLine}` : ''}
            </span>
          </div>
        </div>

        {/* 히어로 그래프 — 데스크톱 · 태블릿은 hand-off 막대, 모바일은 직무 카드 */}
        {mobile ? (
          <JobCards d={d} onTask={goTask} />
        ) : (
          <HandoffBars d={d} cross={cross} tab={tab} onTask={goTask} />
        )}
      </Section>

      {/* Need actions + Team progress / Who needs help */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: desk ? 'repeat(12, minmax(0, 1fr))' : 'minmax(0, 1fr)',
          gap: mobile ? 16 : 24,
          alignItems: 'start',
        }}
      >
        <Section
          d={140}
          style={{
            gridColumn: desk ? 'span 5' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
            padding: v.secPad,
            borderRadius: v.radius,
          }}
          className='bg-green-light03 text-grayscale-gray09'
        >
          <div className='flex items-baseline justify-between gap-4'>
            <div className='flex flex-col gap-1'>
              <h3 className='m-0 text-[20px] font-medium leading-[130%] text-green-deep05'>Need actions</h3>
              <span className='text-[12px] leading-[140%] text-green-deep04'>For you · only you see this</span>
            </div>
            <span
              key={d.na.count}
              title='Same count as Inbox · Needs action'
              className='db-tick text-green-deep05'
              style={{ fontSize: 44, lineHeight: '100%', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}
            >
              {d.na.count}
            </span>
          </div>
          {d.na.count > 0 ? (
            <>
              <div className='flex flex-col'>
                {d.na.rows.map((r, i) => {
                  const warn = r.missed || r.urgent || r.delayed;
                  return (
                    <div
                      key={r.id}
                      className='db-rise grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-t border-green-deep05/[0.14] py-4'
                      style={{ '--d': `${220 + i * 70}ms` }}
                    >
                      <div className='flex min-w-0 flex-col gap-1'>
                        <span className='truncate text-[15px] font-medium leading-[135%] text-grayscale-gray09'>
                          {r.text}
                        </span>
                        <span
                          className={cn(
                            'flex items-center gap-2 text-[13px] leading-[140%]',
                            warn ? 'text-functional-warning04' : 'text-green-deep04',
                          )}
                        >
                          {warn && <span className='h-2 w-2 rounded-full' style={{ background: C.warn }} />}
                          {r.missed ? `Missed · ${r.meta}` : r.meta}
                        </span>
                      </div>
                      <button
                        type='button'
                        onClick={() => ctx.openInboxItem?.(r)}
                        className={cn(
                          'whitespace-nowrap rounded-lg px-3 text-[13px] font-medium transition-[transform,opacity] active:scale-[0.97]',
                          i === 0
                            ? 'bg-green-deep05 text-grayscale-gray00 hover:opacity-90'
                            : 'bg-grayscale-gray00/[0.56] text-green-deep05 hover:bg-grayscale-gray00/80',
                        )}
                        style={{ height: v.btnH }}
                      >
                        {r.btn ?? CTA[r.type] ?? 'Open'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type='button'
                onClick={() => ctx.openInbox?.('action')}
                className='group inline-flex items-center gap-1 self-start text-[14px] font-medium text-green-deep05'
                style={{ minHeight: v.btnH }}
              >
                View all ({d.na.count}) in Inbox
                <ChevronIcon aria-hidden className='h-4 w-4 transition-transform group-hover:translate-x-0.5' />
              </button>
            </>
          ) : (
            <div className='flex flex-col gap-1 border-t border-green-deep05/[0.14] pt-4'>
              <span className='text-[15px] font-medium leading-[135%]'>You’re all caught up.</span>
              <span className='text-[13px] leading-[140%] text-green-deep04'>
                New check-ins, requests and replies show up here.
              </span>
            </div>
          )}
        </Section>

        <Section
          d={200}
          style={{
            gridColumn: desk ? 'span 7' : 'auto',
            display: 'grid',
            gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)',
            gap: mobile ? 28 : 32,
            padding: v.secPad,
            background: C.surface,
            borderRadius: v.radius,
          }}
        >
          <div title={`${d.prog.foot} · ${d.prog.hand}`} className='flex flex-col gap-6'>
            <div className='flex flex-col gap-1'>
              <h3 className='m-0 text-[20px] font-medium leading-[130%]'>Team progress</h3>
              <span className='text-[13px] leading-[140%]' style={{ color: C.sub }}>
                Final tasks submitted
              </span>
            </div>
            <span
              key={d.prog.big}
              className='db-tick'
              style={{ fontSize: 44, lineHeight: '100%', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}
            >
              {d.prog.big}
            </span>
            <div className='grid grid-cols-6 items-end gap-2' style={{ height: 96 }}>
              {d.prog.cols.map((s, i) => (
                <div key={s.k} title={`${s.label} · ${s.n}`} className='flex h-full justify-center'>
                  <div
                    className='flex h-full w-full flex-col justify-end overflow-hidden'
                    style={{ borderRadius: 999, background: C.canvas }}
                  >
                    <span
                      className='db-grow-y db-hi block'
                      style={{
                        '--d': `${320 + i * 60}ms`,
                        height: `${s.h}%`,
                        borderRadius: 999,
                        background: s.color,
                        boxShadow: crossRow && s.k === 'blocked' ? `0 0 0 2px ${C.brand}` : 'none',
                        transition: 'height 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className='grid grid-cols-2 gap-x-4 gap-y-2'>
              {d.prog.cols.map((s) => (
                <span
                  key={s.k}
                  title={`${s.label} · ${s.n}`}
                  className='flex items-center gap-2 text-[12px] leading-[135%]'
                  style={{ color: C.sub }}
                >
                  <span
                    className='h-2 w-2 shrink-0 rounded-[2px]'
                    style={{ background: s.color, boxShadow: `inset 0 0 0 1px ${C.line}` }}
                  />
                  <span className='min-w-0 flex-1' style={{ textWrap: 'balance' }}>
                    {s.label}
                  </span>
                  <span
                    style={{ fontWeight: s.k === 'final' ? 500 : 400, color: C.ink, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {s.n}
                  </span>
                </span>
              ))}
            </div>
          </div>

          <div className='flex flex-col gap-6'>
            <div className='flex items-baseline justify-between gap-4'>
              <h3 className='m-0 text-[20px] font-medium leading-[130%]'>Who needs help</h3>
              <span className='text-[13px]' style={{ color: C.sub }}>
                {d.blk.length} blocked
              </span>
            </div>
            {d.blk.length ? (
              <div className='flex flex-col'>
                {d.blk.map((b, i) => (
                  <div
                    key={b.taskId}
                    onMouseEnter={() => setCross(b.taskId)}
                    onMouseLeave={() => setCross(null)}
                    title={[b.task, b.impact, b.ask].filter(Boolean).join(' · ')}
                    className='db-rise db-hi grid grid-cols-[8px_minmax(0,1fr)_auto] items-start gap-3'
                    style={{
                      '--d': `${300 + i * 80}ms`,
                      padding: '16px 8px',
                      margin: '0 -8px',
                      borderTop: `1px solid ${C.line}`,
                      borderRadius: 8,
                      background: cross === b.taskId ? 'color-mix(in srgb, #0E6B4A 4%, transparent)' : 'transparent',
                    }}
                  >
                    <span className='db-blink mt-1.5 h-2 w-2 rounded-full' style={{ background: C.crit }} />
                    <div className='flex min-w-0 flex-col gap-1'>
                      <span className='flex items-baseline gap-2 text-[14px] font-medium leading-[135%]'>
                        {b.name}
                        <span
                          className='text-[13px] font-normal'
                          style={{ color: C.sub, fontVariantNumeric: 'tabular-nums' }}
                        >
                          {b.wait}
                        </span>
                      </span>
                      <span className='line-clamp-2 text-[13px] leading-[140%]' style={{ color: C.sub }}>
                        {b.body}
                      </span>
                      {b.impact && (
                        <span className='text-[12px] leading-[140%]' style={{ color: C.sub }}>
                          {b.impact}
                        </span>
                      )}
                    </div>
                    {b.isMe ? (
                      <span
                        className='whitespace-nowrap pt-1.5 text-[12px] font-medium leading-[140%]'
                        style={{ color: C.sub }}
                      >
                        Your request
                      </span>
                    ) : (
                      <button
                        type='button'
                        onClick={() => goTask(b.taskId, b.commentId)}
                        title='Opens the blocker comment on this task'
                        className='rounded-lg px-3 text-[13px] font-medium transition-[transform,background-color] hover:bg-grayscale-gray01 active:scale-[0.97]'
                        style={{ height: v.btnH, border: `1px solid ${C.line}`, background: C.surface, color: C.ink }}
                      >
                        Help
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className='flex flex-col gap-1 pt-4' style={{ borderTop: `1px solid ${C.line}` }}>
                <span className='text-[14px] font-medium leading-[135%]'>No one is blocked</span>
                <span className='text-[13px] leading-[140%]' style={{ color: C.sub }}>
                  Blockers raised on tasks show up here.
                </span>
              </div>
            )}
          </div>
        </Section>
      </div>

      {/* Team now */}
      <Section
        d={260}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          padding: v.secPad,
          background: C.surface,
          borderRadius: v.radius,
        }}
      >
        <div className='flex flex-wrap items-baseline gap-x-4 gap-y-2'>
          <h3 className='m-0 text-[20px] font-medium leading-[130%]'>Team now</h3>
          <div className='ml-auto flex items-center gap-4 text-[12px]' style={{ color: C.sub }}>
            <span className='flex items-center gap-2'>
              <span
                style={{ width: 16, height: 8, borderRadius: 2, background: `color-mix(in srgb, ${C.brand} 24%, ${C.surface})` }}
              />
              Work hours
            </span>
            <span className='flex items-center gap-2'>
              <span style={{ width: 0, height: 12, borderLeft: `1px dashed ${C.brand}` }} />
              Task due
            </span>
          </div>
        </div>
        <div className='relative flex flex-col'>
          <div style={{ display: 'grid', gridTemplateColumns: v.laneCols, gap: v.laneGap, height: 24 }}>
            {!mobile && <span />}
            <div style={{ position: 'relative', gridColumn: mobile ? '1 / -1' : 'auto' }}>
              {dues.map((u) => (
                <span
                  key={u.k}
                  style={{
                    position: 'absolute',
                    left: `${u.x}%`,
                    top: 0,
                    transform: 'translateX(-50%)',
                    fontSize: 12,
                    lineHeight: '140%',
                    fontWeight: 500,
                    color: C.brand,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {u.label}
                </span>
              ))}
            </div>
            {!mobile && <span />}
          </div>
          {d.lanes.map((l, i) => (
            <TeamNowLane
              key={l.id}
              l={l}
              i={i}
              hi={!!crossRow && crossRow.byId === l.id}
              mobile={mobile}
              v={v}
              mobileOpen={hover === l.id}
              onMobileToggle={() => setHover(hover === l.id ? null : l.id)}
              viewTasks={viewTasks}
            />
          ))}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: v.laneCols,
              gap: v.laneGap,
              height: 32,
              borderTop: `1px solid ${C.line}`,
            }}
          >
            {!mobile && <span />}
            <div style={{ position: 'relative', gridColumn: mobile ? '1 / -1' : 'auto' }}>
              <span className='absolute left-0 top-2 whitespace-nowrap text-[12px] font-medium leading-[140%]'>
                Now {d.lNow}
              </span>
              {!mobile &&
                d.lTicks.map((k) => (
                  <span
                    key={k.x}
                    className='absolute top-2 whitespace-nowrap text-[12px] leading-[140%]'
                    style={{ left: `${k.x}%`, transform: 'translateX(-50%)', color: C.sub, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {k.label}
                  </span>
                ))}
              <span className='absolute right-0 top-2 whitespace-nowrap text-[12px] font-medium leading-[140%]'>
                {d.lEnd}
              </span>
            </div>
            {!mobile && <span />}
          </div>
          {!mobile && (
            <div
              style={{
                position: 'absolute',
                top: 24,
                bottom: 32,
                left: desk ? 200 : 168,
                right: desk ? 248 : 188,
                pointerEvents: 'none',
              }}
            >
              {d.lDues.map((u) => (
                <span
                  key={u.k}
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${u.x}%`,
                    borderLeft: `1px dashed ${C.brand}`,
                    opacity: 0.5,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* 모바일 — 레인을 누르면 프로필 바텀시트 */}
      {sheetLane && (
        <div
          role='presentation'
          onClick={() => setHover(null)}
          className='db-scrim fixed inset-0 z-[960] flex items-end bg-[rgba(0,0,0,0.4)]'
        >
          <div
            role='dialog'
            aria-modal='true'
            aria-label={sheetLane.name}
            onClick={(e) => e.stopPropagation()}
            className='db-sheet-up flex w-full flex-col gap-3.5 rounded-t-[24px] bg-grayscale-gray00 px-5 pb-7 pt-3 shadow-popup-01'
          >
            <span className='h-1 w-9 self-center rounded-full bg-grayscale-gray03' />
            <div className='flex items-center gap-3'>
              <span className='relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[15px] font-medium text-green-deep04'>
                {sheetLane.ini}
                <span
                  className='absolute bottom-0 right-0 h-3 w-3 rounded-full'
                  style={{
                    background: sheetLane.online ? '#1DC37B' : '#FFFFFF',
                    boxShadow: sheetLane.online
                      ? '0 0 0 2px #FFFFFF'
                      : 'inset 0 0 0 2px #B1ADA8, 0 0 0 2px #FFFFFF',
                  }}
                />
              </span>
              <div className='flex flex-col'>
                <span className='text-[16px] font-medium leading-[140%]'>{sheetLane.name}</span>
                <span className='text-[13px] leading-[140%] text-text-secondary'>{sheetLane.job}</span>
                <span
                  className={cn(
                    'text-[13px] leading-[140%]',
                    sheetLane.online ? 'text-functional-success04' : 'text-text-secondary',
                  )}
                >
                  {sheetLane.card.presence}
                </span>
              </div>
            </div>
            <div className='flex flex-col gap-0.5 rounded-xl bg-green-deep03/[0.04] px-3.5 py-3'>
              <span className='text-[14px] font-medium leading-[140%]' style={{ fontVariantNumeric: 'tabular-nums' }}>
                {sheetLane.card.timeLine}
              </span>
              <span className='text-[13px] leading-[140%] text-text-secondary'>{sheetLane.card.kstLine}</span>
            </div>
            <div className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-2 text-[14px] leading-[140%]'>
              <span className='text-text-secondary'>Now</span>
              <span>{sheetLane.card.task}</span>
              <span className='text-text-secondary'>Hours</span>
              <span>{sheetLane.card.hours}</span>
            </div>
            <div className='flex gap-2'>
              <button
                type='button'
                onClick={() => setHover(null)}
                className='h-11 flex-1 rounded-full bg-green-deep03/[0.08] text-[14px] font-medium text-text-primary'
              >
                Close
              </button>
              <button
                type='button'
                onClick={() => {
                  setHover(null);
                  viewTasks();
                }}
                className='h-11 flex-1 rounded-full bg-green-deep03/[0.08] text-[14px] font-medium text-text-primary'
              >
                View tasks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoDaysDashboard;
